// @ts-nocheck
// ============================================================
// QuamCalendarWorkroom — /square/command-center/calendar (PHASE_2M D3).
//
// FULL WIRING — no mocks, no seedMonth, no localStorage. The month grid, day
// briefing, and event workspace render ONLY live backend records (Google
// events, drafts, plans, Daily calls, daily-meeting records, due tasks), and
// every action calls a real route:
//   create event  → draft → approve (operator tier) → commit (Google-gated)
//   create call   → real controlled Daily room (from draft or directly)
//   create task   → sq_team_tasks with a real due date
//   notes/links/messages → sq_calendar_workspace_items (source-ready
//     migration 20260711000001; until applied the panels show the EXACT
//     MIGRATION_APPROVAL_REQUIRED state)
//   files         → real signed-URL upload/download/delete on the private
//     files bucket (storage failure → STORAGE_PROVIDER_REQUIRED verbatim)
//   command bar   → parses to the SAME real actions; unknown commands get an
//     exact "unsupported" response — never a fake "executed"
// Right-click on days/items opens the shared V2 context menu (real handlers
// or labeled deferrals). Visual language: the operator's Quam_Calendar_Final
// design (DOW-letter day cells, ring-trace day briefing, event workspace).
// SVG icons only — no emoji.
//
// CHAT #1 · V2 SURFACE CONVERSION: this surface now renders on the shared
// `.sqv-surface` foundation (sq-v2-surface.css) and carries all static
// presentation in the Calendar-owned quam-calendar.css. The ONLY inline styles
// left are DYNAMIC values as CSS custom properties (--qcal-accent per kind /
// status, --qcal-dot-size) — the permitted case. Behavior, honesty language,
// receipts, and a11y semantics are unchanged.
// ============================================================

import { useMemo, useRef, useState } from "react";
import { api } from "../../../api/client";
import { FileIcon, LinkIcon, NoteIcon, MessageIcon, MeetingIcon, CalendarIcon, ScheduleIcon, PlusIcon, OpenIcon, TrashIcon, CheckIcon, CopyIcon } from "../shell/icons";
import { useContextMenu } from "../shell/ContextMenu";
import {
  useQuamCalendar, CAL_KIND_META,
  createDraftEvent, approveDraft, commitDraft, createCallFromDraft, createDueTask, setTaskStatus,
  checkAvailability, askQuamToPlan,
  addCalendarItem, removeCalendarItem, attachCalendarFile, openCalendarFile, deleteCalendarFile,
  humanizeCalendarError, monthDateKey,
} from "./calendarData";
import { createConferenceCall, conferenceRoomPath, CALL_MODES } from "../comms/conferenceCallsData";
import PriorityMatrixView from "./PriorityMatrixView";
import "./quam-calendar.css";

/** Tone for a notice line: error when the backend refused / a gate is required. */
const noticeTone = (n) => (n.startsWith("FAILED") || n.includes("REQUIRED") || n.includes("refused") ? "error" : "ok");

function StatusTag({ accent, children, title }) {
  return (
    <span title={title} className="qcal-tag" data-help={title ? "true" : "false"} data-accent={accent}>
      <span className="qcal-tag__dot" />
      {children}
    </span>
  );
}

function Dot({ color, glow, size = 8 }) {
  return <span className="qcal-dot" data-glow={glow ? "true" : "false"} style={{ ["--qcal-accent"]: color, ["--qcal-dot-size"]: size + "px" }} />;
}

/* ═══ STATUS STRIP — real states only ═══ */
function CalendarStatusStrip({ google, itemsState, itemsError, onConnectGoogle }) {
  return (
    <div className="qcal-strip">
      <StatusTag accent="grn" title="Google events, drafts, plans, Daily calls, daily-meeting records, and due tasks all load from live backends.">LIVE BACKEND</StatusTag>
      {google.connected ? (
        <StatusTag accent="grn" title={google.email ? `Connected as ${google.email}` : "Google Calendar connected"}>GOOGLE CONNECTED</StatusTag>
      ) : (
        <button type="button" onClick={onConnectGoogle} className="qcal-action qcal-action--xs" data-accent="ora" title={`Google Calendar is not connected${google.reason ? ` (${google.reason})` : ""}. Drafts still save; committing to Google requires connecting.`}>
          GOOGLE_CALENDAR_NOT_CONNECTED — CONNECT
        </button>
      )}
      {itemsState === "live" && <StatusTag accent="cyan" title="Notes / links / files / messages persist to sq_calendar_workspace_items.">NOTES · LINKS · FILES · MESSAGES LIVE</StatusTag>}
      {itemsState === "migration_required" && <StatusTag accent="gld" title={itemsError || "Apply migration 20260711000001 to power the notes/links/files/messages panels."}>ITEMS: MIGRATION_APPROVAL_REQUIRED</StatusTag>}
      {itemsState === "error" && <StatusTag accent="red" title={itemsError || ""}>ITEMS: LOAD FAILED</StatusTag>}
    </div>
  );
}

/* ═══ DAY CELL — every day clickable (empty days open the create sheet) ═══ */
function DayCell({ day, selected, isToday, onClick, onContextMenu }) {
  const count = day.items.length;
  return (
    <button
      type="button"
      className="qcal-daycell"
      data-selected={selected ? "true" : "false"}
      data-today={isToday ? "true" : "false"}
      data-has-items={count > 0 ? "true" : "false"}
      onClick={onClick}
      onContextMenu={onContextMenu}
      aria-label={`Day ${day.date}${isToday ? ", today" : ""}${selected ? ", selected" : ""}, ${count} ${count === 1 ? "item" : "items"}`}
      aria-current={isToday ? "date" : undefined}
      title={count === 0 ? "Open this day — create real work here" : `${count} item(s)`}
    >
      <span className="qcal-daycell__letter">{day.letter}</span>
      {count > 0 && (
        <span className="qcal-daycell__dots">
          {day.items.slice(0, 4).map((it, i) => <Dot key={i} color={CAL_KIND_META[it.kind].color} glow={selected} size={4} />)}
        </span>
      )}
      <span className="qcal-daycell__foot">
        {count > 0
          ? <><span className="qcal-daycell__count">{count}</span><span className="qcal-daycell__date">{day.date}</span></>
          : <span className="qcal-daycell__date--empty">{day.date}</span>}
      </span>
    </button>
  );
}

/* ═══ CREATE ACTION SHEET — every action is a real call ═══ */
function CreateSheet({ day, monthLabel, busy, notice, onCreateEvent, onCreateCall, onCreateTask, onAddNote, onAddLink, onAttachFile, onAddMessage, onCheckAvailability, onAskQuam, itemsState }) {
  const [kind, setKind] = useState(null); // which inline form is open
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("09:00");
  const [body, setBody] = useState("");
  const [callMode, setCallMode] = useState("internal_operator");
  const [callDur, setCallDur] = useState(30);
  const fileRef = useRef(null);

  const itemGated = itemsState !== "live";
  const gate = itemsState === "migration_required"
    ? "MIGRATION_APPROVAL_REQUIRED — apply 20260711000001 first."
    : "Items backend failed to load — retry after reload.";

  const submit = async () => {
    const t = title.trim();
    if (!t && kind !== "availability" ) return;
    if (kind === "event") await onCreateEvent({ title: t, time });
    else if (kind === "call") await onCreateCall(t, callMode, callDur);
    else if (kind === "task") await onCreateTask(t);
    else if (kind === "note") await onAddNote(t, body.trim());
    else if (kind === "link") await onAddLink(t, body.trim());
    else if (kind === "message") await onAddMessage(t, body.trim());
    else if (kind === "plan") await onAskQuam(t);
    setTitle(""); setBody(""); setKind(null);
  };

  return (
    <div className="qcal-create">
      <div className="qcal-create__label">CREATE ON {monthLabel.toUpperCase()} {day.date}</div>
      <div className="qcal-btnrow">
        <button className="qcal-action" data-accent="pur" onClick={() => setKind(kind === "event" ? null : "event")}><CalendarIcon size={12} still /> CREATE EVENT</button>
        <button className="qcal-action" data-accent="red" onClick={() => setKind(kind === "call" ? null : "call")}><MeetingIcon size={12} still /> CREATE CONFERENCE CALL</button>
        <button className="qcal-action" data-accent="gld" onClick={() => setKind(kind === "task" ? null : "task")}><CheckIcon size={12} /> CREATE TASK</button>
        <button className="qcal-action" data-accent="ylw" disabled={itemGated} title={itemGated ? gate : undefined} onClick={() => setKind(kind === "note" ? null : "note")}><NoteIcon size={12} /> ADD NOTE</button>
        <button className="qcal-action" data-accent="cyan" disabled={itemGated} title={itemGated ? gate : undefined} onClick={() => setKind(kind === "link" ? null : "link")}><LinkIcon size={12} /> ATTACH LINK</button>
        <button className="qcal-action" data-accent="cyan" disabled={itemGated} title={itemGated ? gate : undefined} onClick={() => fileRef.current?.click()}><FileIcon size={12} /> ATTACH FILE</button>
        <button className="qcal-action" data-accent="grn" disabled={itemGated} title={itemGated ? gate : undefined} onClick={() => setKind(kind === "message" ? null : "message")}><MessageIcon size={12} still /> ADD MESSAGE</button>
        <button className="qcal-action" data-accent="w2" onClick={onCheckAvailability}><ScheduleIcon size={12} /> CHECK AVAILABILITY</button>
        <button className="qcal-action" data-accent="grn" onClick={() => setKind(kind === "plan" ? null : "plan")}><PlusIcon size={12} /> ASK QUAM TO PLAN</button>
      </div>
      <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onAttachFile(f); e.target.value = ""; }} />
      {kind && (
        <div className="qcal-form">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder={kind === "link" ? "Link label…" : kind === "plan" ? "What should Quam plan?" : kind === "message" ? "From / context…" : kind === "call" ? "Meeting title…" : "Title…"} className="qcal-input" />
          {kind === "event" && (
            <label className="qcal-form__row">
              START <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="qcal-input qcal-input--time" />
              <span>60 min · saved as a DRAFT (approve, then commit to Google)</span>
            </label>
          )}
          {kind === "call" && (
            <>
              <div className="qcal-fieldrow">
                <label className="qcal-field qcal-field--mode">
                  MEETING TYPE
                  <select value={callMode} onChange={(e) => setCallMode(e.target.value)} className="qcal-input qcal-input--select">
                    {CALL_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </label>
                <label className="qcal-field qcal-field--dur">
                  DURATION
                  <select value={callDur} onChange={(e) => setCallDur(Number(e.target.value))} className="qcal-input qcal-input--select">
                    {[15, 30, 45, 60, 90].map((d) => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </label>
              </div>
              <span className="qcal-note">Creates a REAL controlled Daily room now (audited + receipted). Join it from this day&apos;s briefing — no external invitations are sent.</span>
            </>
          )}
          {(kind === "note" || kind === "message") && <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={kind === "note" ? "Note body…" : "Message body…"} className="qcal-input qcal-input--area" />}
          {kind === "link" && <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="https://…" className="qcal-input" />}
          <div className="qcal-btnrow qcal-btnrow--tight">
            <button className="qcal-action" data-accent="grn" disabled={busy} onClick={submit}>{busy ? "SAVING…" : "SAVE"}</button>
            <button className="qcal-action" data-accent="w3" onClick={() => { setKind(null); setTitle(""); setBody(""); }}>CANCEL</button>
          </div>
        </div>
      )}
      {notice && <p className="qcal-notice" data-tone={noticeTone(notice)}>{notice}</p>}
    </div>
  );
}

/* ═══ DAY BRIEFING — level 2 ═══ */
function DayBriefing({ day, monthLabel, onOpenItem, sheetProps, menu, itemMenuActions }) {
  return (
    <div className="qcal-briefing">
      <div className="qcal-briefing__inner">
        <div className="qcal-briefing__head">
          <div className="qcal-briefing__date">{monthLabel} {day.date}</div>
          <div className="qcal-briefing__count">{day.items.length} scheduled</div>
        </div>

        {day.items.length === 0 && (
          <div className="qcal-briefing__empty">
            Nothing on the books — live and genuinely empty. Create something real below.
          </div>
        )}

        {day.items.map((item) => {
          const tc = CAL_KIND_META[item.kind];
          return (
            <button
              key={item.key}
              onClick={() => onOpenItem(item)}
              onContextMenu={(e) => menu.open(e, itemMenuActions(item), `${tc.label} — ${item.title}`)}
              className="qcal-item"
            >
              <Dot color={tc.color} glow />
              <span>
                <span className="qcal-item__title">{item.title}</span>
                <span className="qcal-item__meta">
                  {item.timeLabel} · {tc.label}{item.status ? ` · ${String(item.status).replace(/_/g, " ")}` : ""}
                </span>
              </span>
              <OpenIcon size={12} color="var(--qcal-w3)" />
            </button>
          );
        })}

        <CreateSheet day={day} monthLabel={monthLabel} {...sheetProps} />
      </div>
    </div>
  );
}

/* ═══ ITEMS PANEL — notes/links/files/messages for one anchor (REAL rows) ═══ */
function AnchorItemsPanel({ color, type, rows, itemsState, itemsError, onAdd, onRemove, onOpenFile, busy }) {
  const [text, setText] = useState("");
  const [aux, setAux] = useState("");
  const fileRef = useRef(null);
  const gated = itemsState !== "live";
  return (
    <div className="qcal-panel" style={{ ["--qcal-accent"]: color }}>
      {gated ? (
        <div className="qcal-panel__gated">
          {itemsState === "migration_required"
            ? (itemsError || "MIGRATION_APPROVAL_REQUIRED — apply migration 20260711000001_calendar_workspace_items.sql to power this panel.")
            : (itemsError || "Items backend failed to load.")}
        </div>
      ) : (
        <>
          {rows.length === 0 && <div className="qcal-panel__empty">None yet — saved rows appear here (real records, owner-scoped).</div>}
          {rows.map((r) => (
            <div key={r.id} className="qcal-panel__row">
              <span className="qcal-panel__rowmain">
                <span className="qcal-panel__rowtitle">{r.title || r.body || r.url || r.file_name}</span>
                {type === "note" && r.body && r.title && <span className="qcal-panel__sub qcal-panel__sub--note">{r.body}</span>}
                {type === "link" && r.url && <span className="qcal-panel__sub qcal-panel__sub--link">{r.url}</span>}
                {type === "message" && r.body && <span className="qcal-panel__sub qcal-panel__sub--msg">{r.body}</span>}
              </span>
              {type === "link" && r.url && <button className="qcal-action" onClick={() => window.open(r.url, "_blank", "noopener")}><OpenIcon size={11} /> OPEN</button>}
              {type === "file" && <button className="qcal-action" onClick={() => onOpenFile(r)}><OpenIcon size={11} /> VIEW</button>}
              <button className="qcal-action" data-accent="red" title="Deletes the real row (audited)." onClick={() => onRemove(r)}><TrashIcon size={11} /></button>
            </div>
          ))}
          {type === "file" ? (
            <>
              <button className="qcal-action qcal-action--block" disabled={busy} onClick={() => fileRef.current?.click()}>
                <FileIcon size={12} /> {busy ? "UPLOADING…" : "ATTACH FILE (REAL UPLOAD)"}
              </button>
              <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onAdd(f); e.target.value = ""; }} />
            </>
          ) : (
            <div className="qcal-panel__form">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={type === "note" ? "Write a note…" : type === "link" ? "Label…" : "Message…"} className="qcal-input"
                onKeyDown={(e) => { if (e.key === "Enter" && type !== "link") { onAdd(text, aux); setText(""); setAux(""); } }} />
              {type === "link" && <input value={aux} onChange={(e) => setAux(e.target.value)} placeholder="https://…" className="qcal-input" />}
              <button className="qcal-action" disabled={busy || !text.trim() || (type === "link" && !aux.trim())} onClick={() => { onAdd(text, aux); setText(""); setAux(""); }}>
                {busy ? "SAVING…" : "SAVE (REAL ROW)"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══ COMMAND BAR — parses to REAL actions only ═══ */
function CommandBar({ onCommand }) {
  const [input, setInput] = useState("");
  const [log, setLog] = useState([]);
  const run = async () => {
    const cmd = input.trim();
    if (!cmd) return;
    setInput("");
    const result = await onCommand(cmd);
    setLog((p) => [...p.slice(-4), { cmd, result }]);
  };
  return (
    <div className="qcal-cmd">
      <div className="qcal-cmd__ring">
        <div className="qcal-cmd__inner">
          <span className="qcal-cmd__q">Q</span>
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder='task: … | note: … | event: … @ 14:00 | call: … | plan: … | availability'
            className="qcal-cmd__input" />
        </div>
      </div>
      {log.length > 0 && (
        <div className="qcal-cmd__log">
          {log.map((c, i) => (
            <div key={i} className="qcal-cmd__logline">
              <span className="qcal-cmd__cmd">{c.cmd}</span>{" "}
              <span className="qcal-cmd__result" data-ok={c.result.ok ? "true" : "false"}>{c.result.ok ? `saved — ${c.result.note}` : `NOT executed — ${c.result.note}`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ EVENT WORKSPACE — level 3 (real record + real panels + real actions) ═══ */
function EventWorkspace({ item, monthLabel, itemsState, itemsError, itemRows, onBack, onAction, onAddItem, onAttachFile, onRemoveItem, onOpenFile, busy, notice }) {
  const tc = CAL_KIND_META[item.kind];
  const [openPanel, setOpenPanel] = useState(null);
  const anchorRows = (t) => itemRows.filter((r) => r.anchor_kind === item.anchorKind && r.anchor_id === item.id && r.item_type === t);
  const modules = [
    { key: "file", label: "Files", color: "var(--qcal-cyan)" },
    { key: "link", label: "Links", color: "var(--qcal-pur)" },
    { key: "note", label: "Notes", color: "var(--qcal-ora)" },
    { key: "message", label: "Messages", color: "var(--qcal-grn)" },
  ];

  const raw = item.raw || {};
  const draftStatus = String(raw.status || "");
  return (
    <div className="qcal-event">
      <button onClick={onBack} className="qcal-action qcal-event__back" data-accent="pur">BACK TO {monthLabel.toUpperCase()}</button>

      <div className="qcal-event__kindrow">
        <Dot color={tc.color} glow size={10} />
        <span className="qcal-event__kind" style={{ ["--qcal-accent"]: tc.color }}>{tc.label.toUpperCase()}</span>
        <span className="qcal-event__meta">· {item.timeLabel}{item.status ? ` · ${String(item.status).replace(/_/g, " ")}` : ""}</span>
      </div>
      <h1 className="qcal-event__title">{item.title}</h1>
      {item.detail && <div className="qcal-event__detail">{item.detail}</div>}

      {/* REAL per-kind actions */}
      <div className="qcal-event__actions">
        {item.kind === "draft" && ["draft", "needs_approval", "failed"].includes(draftStatus) && (
          <button className="qcal-action" data-accent="pur" disabled={busy} onClick={() => onAction("approve")} title="Approval authorizes the external Google write — operator tier (level 80). A refusal is shown verbatim.">APPROVE DRAFT</button>
        )}
        {item.kind === "draft" && draftStatus === "approved" && (
          <button className="qcal-action" data-accent="grn" disabled={busy} onClick={() => onAction("commit")} title="Writes the REAL Google Calendar event (requires the Google connection).">COMMIT TO GOOGLE</button>
        )}
        {item.kind === "draft" && (
          <button className="qcal-action" data-accent="red" disabled={busy} onClick={() => onAction("draft_call")} title="Creates a REAL controlled Daily room bound to this draft.">CREATE CONFERENCE CALL</button>
        )}
        {item.kind === "google_event" && raw.htmlLink && (
          <button className="qcal-action" data-accent="cyan" onClick={() => window.open(String(raw.htmlLink), "_blank", "noopener")}><OpenIcon size={11} /> OPEN IN GOOGLE</button>
        )}
        {item.kind === "call" && ["created", "token_ready", "active"].includes(String(raw.status)) && (
          <button className="qcal-action" data-accent="grn" disabled={busy} onClick={() => onAction("join_call")}>JOIN (SCOPED TOKEN)</button>
        )}
        {item.kind === "task" && String(raw.status) !== "done" && (
          <button className="qcal-action" data-accent="grn" disabled={busy} onClick={() => onAction("task_done")}><CheckIcon size={11} /> MARK DONE</button>
        )}
        {item.kind === "task" && (
          <button className="qcal-action" data-accent="red" disabled={busy} onClick={() => onAction("task_blocked")}>MARK BLOCKED</button>
        )}
        <button className="qcal-action" data-accent="w2" onClick={() => onAction("copy")}><CopyIcon size={11} /> COPY REF</button>
      </div>
      {notice && <p className="qcal-notice qcal-notice--tight" data-tone={noticeTone(notice)}>{notice}</p>}

      <div className="qcal-event__modules">
        {modules.map((m) => {
          const count = itemsState === "live" ? anchorRows(m.key).length : "–";
          return (
            <button key={m.key} onClick={() => setOpenPanel(openPanel === m.key ? null : m.key)} className="qcal-module" data-open={openPanel === m.key ? "true" : "false"} style={{ ["--qcal-accent"]: m.color }}>
              <span className="qcal-module__label">{m.label}</span>
              <span className="qcal-module__count">{count}</span>
            </button>
          );
        })}
      </div>

      {openPanel && (
        <AnchorItemsPanel
          color={modules.find((m) => m.key === openPanel).color}
          type={openPanel}
          rows={anchorRows(openPanel)}
          itemsState={itemsState}
          itemsError={itemsError}
          busy={busy}
          onAdd={(a, b) => openPanel === "file" ? onAttachFile(a) : onAddItem(openPanel, a, b)}
          onAttachFile={onAttachFile}
          onRemove={onRemoveItem}
          onOpenFile={onOpenFile}
        />
      )}
    </div>
  );
}

/* ═══ MAIN ═══ */
export default function QuamCalendarWorkroom({ onExit, onHome, onOpenPath }) {
  const now = useRef(new Date()).current;
  const [view, setView] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [viewMode, setViewMode] = useState("month"); // "month" | "matrix"
  const { year, month } = view;
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const viewDate = new Date(year, month, 1);
  const monthLabel = viewDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const monthWord = viewDate.toLocaleDateString("en-US", { month: "long" });

  const cal = useQuamCalendar(year, month);
  const menu = useContextMenu();
  const [selectedKey, setSelectedKey] = useState(null);
  const [workItem, setWorkItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const goMonth = (delta) => { setSelectedKey(null); setWorkItem(null); setNotice(null); setView((v) => { const d = new Date(v.year, v.month + delta, 1); return { year: d.getFullYear(), month: d.getMonth() }; }); };
  const goToday = () => { setSelectedKey(null); setWorkItem(null); setNotice(null); setView({ year: now.getFullYear(), month: now.getMonth() }); };

  const selDay = useMemo(() => cal.days.find((d) => d.dateKey === selectedKey) || null, [cal.days, selectedKey]);
  const todayKey = isCurrentMonth ? monthDateKey(year, month, now.getDate()) : null;
  const mondayOffset = cal.days.length > 0 ? (cal.days[0].dow + 6) % 7 : 0;

  const act = async (fn, okNote) => {
    setBusy(true); setNotice(null);
    try {
      const r = await fn();
      if (r && r.ok === false) setNotice(`FAILED — ${r.reason || "rejected"}${r.message ? `: ${r.message}` : ""}${r.missing_scopes?.length ? ` (missing scopes: ${r.missing_scopes.join(", ")})` : ""}`);
      else { setNotice(okNote); cal.reload(); }
    } catch (err) {
      setNotice(`FAILED — ${err instanceof Error ? err.message : humanizeCalendarError(err)}`);
    } finally { setBusy(false); }
  };

  const dayAnchor = (day) => ({ anchor_kind: "day", anchor_id: day.dateKey, anchor_date: day.dateKey });

  const sheetProps = selDay ? {
    busy, notice, itemsState: cal.itemsState,
    onCreateEvent: ({ title, time }) => act(() => createDraftEvent({ title, dateKey: selDay.dateKey, startTime: time, durationMinutes: 60 }), "draft saved (sq_quam_calendar_event_drafts) — approve + commit to reach Google"),
    onCreateCall: (title, mode, dur) => act(() => createConferenceCall({ title, meeting_mode: mode || "internal_operator", duration_minutes: dur || 30 }), "controlled Daily room created (audited + receipted)"),
    onCreateTask: (title) => act(() => createDueTask(title, selDay.dateKey), "task saved with a real due date (sq_team_tasks, audited)"),
    onAddNote: (title, body) => act(() => addCalendarItem({ ...dayAnchor(selDay), item_type: "note", title, body }), "note saved (sq_calendar_workspace_items)"),
    onAddLink: (title, url) => act(() => addCalendarItem({ ...dayAnchor(selDay), item_type: "link", title, url }), "link saved (sq_calendar_workspace_items)"),
    onAddMessage: (title, body) => act(() => addCalendarItem({ ...dayAnchor(selDay), item_type: "message", title, body }), "message saved (sq_calendar_workspace_items)"),
    onAttachFile: (file) => act(() => attachCalendarFile(file, dayAnchor(selDay)), "file uploaded to the private files bucket + recorded"),
    onCheckAvailability: () => act(() => checkAvailability(selDay.dateKey), "free/busy returned from the live Google runtime"),
    onAskQuam: (request) => act(() => askQuamToPlan(request, selDay.dateKey), "planning request saved (sq_calendar_plans)"),
  } : null;

  const itemMenuActions = (item) => {
    const acts = [
      { id: "open", label: "Open", icon: <OpenIcon size={13} />, onSelect: () => setWorkItem(item) },
      { id: "copy", label: "Copy Ref", icon: <CopyIcon size={13} />, onSelect: () => { void navigator.clipboard?.writeText(`${item.title} — ${item.dateKey} ${item.timeLabel} (${item.kind}:${item.id})`); } },
    ];
    if (item.kind === "task" && String(item.raw?.status) !== "done") acts.push({ id: "done", label: "Mark Done", icon: <CheckIcon size={13} />, onSelect: () => act(() => setTaskStatus(item.id, "done"), "task done (audited)"), hint: "Real audited status change" });
    if (item.kind === "draft") acts.push({ id: "approve", label: "Approve Draft", icon: <CheckIcon size={13} />, onSelect: () => act(() => approveDraft(item.id), "draft approved"), hint: "Operator tier — refusals shown verbatim" });
    if (item.kind === "call") acts.push({ id: "join", label: "Join Call", icon: <MeetingIcon size={13} still />, onSelect: () => onOpenPath?.(conferenceRoomPath(item.id)), hint: "Opens the in-app conference room; the scoped token is minted there in memory" });
    acts.push({ id: "email", label: "Email", icon: <MessageIcon size={13} still />, onSelect: () => { window.location.href = `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(`${item.dateKey} ${item.timeLabel} — ${item.kind}`)}`; } });
    acts.push({ id: "publish", label: "Publish to Portal", icon: <OpenIcon size={13} />, deferred: "INTEGRATION_DEFERRED", hint: "Calendar-to-portal publishing has no route yet" });
    if (item.kind === "google_event") acts.push({ id: "delete", label: "Delete", icon: <TrashIcon size={13} />, danger: true, deferred: "INTEGRATION_DEFERRED", hint: "No Google delete route exists — never faked" });
    return acts;
  };

  const dayMenuActions = (day) => [
    { id: "open", label: "Open Day", icon: <OpenIcon size={13} />, onSelect: () => setSelectedKey(day.dateKey) },
    { id: "event", label: "Create Event (draft)", icon: <CalendarIcon size={13} still />, onSelect: () => setSelectedKey(day.dateKey) },
    { id: "task", label: "Create Task", icon: <CheckIcon size={13} />, onSelect: () => setSelectedKey(day.dateKey) },
    { id: "avail", label: "Check Availability", icon: <ScheduleIcon size={13} />, onSelect: () => { setSelectedKey(day.dateKey); void act(() => checkAvailability(day.dateKey), "free/busy returned from the live Google runtime"); } },
  ];

  // Command bar — parses to the SAME real actions; anything else is exactly refused.
  const onCommand = async (cmd) => {
    const day = selDay || cal.days.find((d) => d.dateKey === todayKey) || cal.days[0];
    const m = /^(task|note|event|call|plan|link)\s*:\s*(.+)$/i.exec(cmd);
    try {
      if (/^availability$/i.test(cmd)) { await checkAvailability(day.dateKey); return { ok: true, note: "free/busy returned (live Google runtime)" }; }
      if (!m) return { ok: false, note: 'unsupported command — use "task: …", "note: …", "event: … @ HH:MM", "call: …", "link: label https://…", "plan: …", or "availability"' };
      const kind = m[1].toLowerCase(); const rest = m[2].trim();
      if (kind === "task") { await createDueTask(rest, day.dateKey); return { ok: true, note: `task saved for ${day.dateKey} (sq_team_tasks)` }; }
      if (kind === "note") {
        if (cal.itemsState !== "live") return { ok: false, note: "MIGRATION_APPROVAL_REQUIRED — apply 20260711000001 to save notes" };
        await addCalendarItem({ ...dayAnchor(day), item_type: "note", title: rest.slice(0, 80), body: rest }); return { ok: true, note: "note saved (sq_calendar_workspace_items)" };
      }
      if (kind === "link") {
        if (cal.itemsState !== "live") return { ok: false, note: "MIGRATION_APPROVAL_REQUIRED — apply 20260711000001 to save links" };
        const lm = /^(.*?)\s+(https?:\/\/\S+)$/.exec(rest);
        if (!lm) return { ok: false, note: 'link needs a URL — "link: label https://…"' };
        await addCalendarItem({ ...dayAnchor(day), item_type: "link", title: lm[1] || lm[2], url: lm[2] }); return { ok: true, note: "link saved" };
      }
      if (kind === "event") {
        const tm = /^(.*?)(?:\s*@\s*(\d{1,2}:\d{2}))?$/.exec(rest);
        const r = await createDraftEvent({ title: tm[1].trim(), dateKey: day.dateKey, startTime: tm[2] || "09:00", durationMinutes: 60 });
        return r.ok ? { ok: true, note: "event DRAFT saved — approve + commit to reach Google" } : { ok: false, note: r.reason || "draft rejected" };
      }
      if (kind === "call") {
        const r = await createConferenceCall({ title: rest, meeting_mode: "internal_operator", duration_minutes: 30 });
        return r.ok ? { ok: true, note: "controlled Daily room created" } : { ok: false, note: r.reason };
      }
      if (kind === "plan") { const r = await askQuamToPlan(rest, day.dateKey); return r.ok !== false ? { ok: true, note: "planning request saved (sq_calendar_plans)" } : { ok: false, note: r.reason || "plan rejected" }; }
      return { ok: false, note: "unsupported command" };
    } catch (err) {
      return { ok: false, note: err instanceof Error ? err.message : "backend rejected the command" };
    } finally { cal.reload(); }
  };

  const shellBody = (children) => (
    <div className="sqv-surface qcal" data-sqv-surface="quam-calendar">
      <div className="qcal__glow" />
      <div className="qcal-topbar">
        <button onClick={onHome} title="Home — back to the Command Center" aria-label="Home — Command Center" className="qcal-home">Q</button>
        <button onClick={onExit} className="qcal-action qcal-action--sm2" data-accent="w3">BACK TO COMMAND CENTER</button>
        <span className="qcal-spacer" />
        <button onClick={() => cal.reload()} className="qcal-action qcal-action--sm2" data-accent="w3">REFRESH</button>
      </div>
      <CalendarStatusStrip
        google={cal.google}
        itemsState={cal.itemsState}
        itemsError={cal.itemsError}
        onConnectGoogle={async () => {
          const r = await api.quamCalendar.connectCalendar().catch(() => null);
          if (r?.ok && r.auth_url) window.location.href = r.auth_url;
          else setNotice(`FAILED — Google connect unavailable${r?.reason ? `: ${r.reason}` : ""}`);
        }}
      />
      <div className="qcal-body">{children}</div>
      {menu.element}
    </div>
  );

  if (cal.authRequired) {
    return shellBody(
      <div className="qcal-empty">
        Sign in to the live cockpit to load the calendar — nothing is shown until then (no fabricated events).
      </div>,
    );
  }
  if (cal.loading) {
    return shellBody(<div className="qcal-empty">Loading the live calendar…</div>);
  }

  if (workItem) {
    return shellBody(
      <EventWorkspace
        item={workItem}
        monthLabel={monthWord}
        itemsState={cal.itemsState}
        itemsError={cal.itemsError}
        itemRows={cal.items}
        busy={busy}
        notice={notice}
        onBack={() => { setWorkItem(null); setNotice(null); }}
        onAction={(kind) => {
          if (kind === "approve") return act(() => approveDraft(workItem.id), "draft approved — commit to write the Google event");
          if (kind === "commit") return act(() => commitDraft(workItem.id), "committed — REAL Google Calendar event created");
          if (kind === "draft_call") return act(() => createCallFromDraft(workItem.id), "controlled Daily room created from this draft");
          if (kind === "join_call") { onOpenPath?.(conferenceRoomPath(workItem.id)); return; }
          if (kind === "task_done") return act(() => setTaskStatus(workItem.id, "done"), "task done (audited)");
          if (kind === "task_blocked") return act(() => setTaskStatus(workItem.id, "blocked"), "task blocked (audited)");
          if (kind === "copy") { void navigator.clipboard?.writeText(`${workItem.title} — ${workItem.dateKey} (${workItem.kind}:${workItem.id})`); setNotice("reference copied"); }
        }}
        onAddItem={(type, a, b) => act(() => addCalendarItem({ anchor_kind: workItem.anchorKind, anchor_id: workItem.id, anchor_date: workItem.dateKey, item_type: type, title: a, body: type === "link" ? undefined : b, url: type === "link" ? b : undefined }), `${type} saved (sq_calendar_workspace_items)`)}
        onAttachFile={(file) => act(() => attachCalendarFile(file, { anchor_kind: workItem.anchorKind, anchor_id: workItem.id, anchor_date: workItem.dateKey }), "file uploaded + recorded")}
        onRemoveItem={(row) => act(() => (row.item_type === "file" ? deleteCalendarFile(row) : removeCalendarItem(row.id)), "item deleted (audited)")}
        onOpenFile={(row) => act(() => openCalendarFile(row), "signed download URL opened")}
      />,
    );
  }

  return shellBody(
    <div className="qcal-main">
      <div className="qcal-header">
        <div>
          <div className="qcal-kicker">QUAM CALENDAR</div>
          <h1 className="qcal-h1">{monthLabel}</h1>
        </div>
        <div className="qcal-legend">
          {Object.entries(CAL_KIND_META).map(([k, v]) => (
            <span key={k} className="qcal-legend__item">
              <Dot color={v.color} size={6} glow />
              <span className="qcal-legend__label" style={{ ["--qcal-accent"]: v.color }}>{v.label}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="qcal-nav">
        <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month" className="qcal-action qcal-action--sm" data-accent="w3">‹ PREV</button>
        <button type="button" onClick={goToday} disabled={isCurrentMonth} title={isCurrentMonth ? "Already on the current month" : "Jump to the current month"} className="qcal-action qcal-action--sm qcal-action--today" data-accent="ora">TODAY</button>
        <button type="button" onClick={() => goMonth(1)} aria-label="Next month" className="qcal-action qcal-action--sm" data-accent="w3">NEXT ›</button>
        <span className="qcal-spacer" />
        <div role="tablist" aria-label="Calendar view" className="qcal-tabs">
          <button type="button" role="tab" id="qcal-tab-month" aria-controls="qcal-viewpanel" aria-selected={viewMode === "month"} onClick={() => setViewMode("month")} className="qcal-tab" data-accent="pur"><CalendarIcon size={12} still /> MONTH</button>
          <button type="button" role="tab" id="qcal-tab-matrix" aria-controls="qcal-viewpanel" aria-selected={viewMode === "matrix"} onClick={() => setViewMode("matrix")} title="Eisenhower priority quadrants — importance × urgency from task priority + due date (replaceable policy)" className="qcal-tab" data-accent="gld"><ScheduleIcon size={12} /> PRIORITY MATRIX</button>
        </div>
      </div>

      {cal.error && <p className="qcal-error">{cal.error}</p>}

      <div role="tabpanel" id="qcal-viewpanel" aria-labelledby={viewMode === "month" ? "qcal-tab-month" : "qcal-tab-matrix"}>
      {viewMode === "matrix" ? (
        <PriorityMatrixView items={cal.days.flatMap((d) => d.items)} onOpenItem={(item) => { setWorkItem(item); setNotice(null); }} />
      ) : (<>
      <div className="qcal-dow">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="qcal-dow__cell">{d.toUpperCase()}</div>
        ))}
      </div>

      <div className="qcal-grid" data-open={selDay ? "true" : "false"}>
        {Array.from({ length: mondayOffset }).map((_, i) => <div key={"blank" + i} />)}
        {cal.days.map((day) => (
          <DayCell
            key={day.dateKey}
            day={day}
            selected={selectedKey === day.dateKey}
            isToday={day.dateKey === todayKey}
            onClick={() => { setSelectedKey(selectedKey === day.dateKey ? null : day.dateKey); setNotice(null); }}
            onContextMenu={(e) => menu.open(e, dayMenuActions(day), `${monthWord} ${day.date}`)}
          />
        ))}
      </div>

      {selDay && (
        <DayBriefing
          day={selDay}
          monthLabel={monthWord}
          onOpenItem={(item) => { setWorkItem(item); setNotice(null); }}
          sheetProps={sheetProps}
          menu={menu}
          itemMenuActions={itemMenuActions}
        />
      )}
      </>)}
      </div>

      <CommandBar onCommand={onCommand} />
    </div>,
  );
}
